import {
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  useColorModeValue,
  Box,
  InputLeftElement,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  selectFilterValues,
  updateFilterValues,
  setNewFiltersSet,
} from '../../../store/redux/slices/passList.slice';
import { CustomSearchIcon } from '../../../theme/icons/CustomSearchIcon';

export function CustomPassIdSearchBar() {
  const dispatch = useAppDispatch();
  const filterValues = useAppSelector(selectFilterValues);
  const modeBgColor = useColorModeValue('white', 'secondaryDark');
  const [searchValue, setSearchValue] = useState('');

  const dispatchSearch = useCallback(
    (value: string) => {
      dispatch(
        updateFilterValues({
          ...filterValues,
          id: '',
          extId: '',
          extIdPrefix: '',
          search: value,
        })
      );
      dispatch(setNewFiltersSet(true));
    },
    [dispatch, filterValues]
  );

  const debouncedSearch = useMemo(
    () => debounce((value: string) => dispatchSearch(value), 300),
    [dispatchSearch]
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value.trim());
  };

  const handleClear = () => {
    setSearchValue('');
    debouncedSearch.cancel();
    dispatchSearch('');
  };

  return (
    <InputGroup w={{ base: '100%', sm: '60%' }} mr={2}>
      <InputLeftElement
        height="full"
        justifyContent="start"
        children={<CustomSearchIcon name="search" boxSize="16px" />}
        pl="12px"
      />
      <Input
        borderRadius="5px"
        _focus={{ borderColor: '#3A4A57', borderWidth: '2px' }}
        alignSelf={{ base: 'flex-start', sm: 'center' }}
        pl="35px"
        pr={searchValue ? '75px' : '40px'}
        fontSize="13px"
        fontWeight="bold"
        w="full"
        bg={modeBgColor}
        height="38px"
        type="text"
        placeholder="Search by external ID or UUID"
        _placeholder={{ color: '#989898', letterSpacing: '0.3px' }}
        value={searchValue}
        onChange={handleInputChange}
      />
      {searchValue && (
        <InputRightElement height="36px" width="40px" pr="4px">
          <IconButton
            aria-label="Clear search"
            icon={<CloseIcon boxSize="10px" />}
            size="sm"
            variant="ghost"
            color="#989898"
            _hover={{ bg: 'none', color: '#4C4C4C' }}
            onClick={handleClear}
          />
        </InputRightElement>
      )}
    </InputGroup>
  );
}
